import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { format, isAfter, isBefore, isValid, max, parseISO, sub } from "date-fns";
import VKButton from "components/vkButton";
import { dateFormats } from "utils/formats";
import { LocalizeText } from "components/localizer";
import { TFormEvent } from "utils/types";
import { ListItemText, List, ListItem, Box, Typography } from "@mui/material";
import ArrowIcon from "@mui/icons-material/ArrowForward";
import { fetchProductsAvailability } from "adapters/membershipAdapter";
import { MembershipsContext } from "contexts/memberships";
import { ISubscription } from "reducers/subscriptions";
import { usePermission } from "utils/hooks/usePermission";
import { TLanguageTag } from "contexts/language";
import { DatePicker } from "components/datePicker";
import { Clear } from "@mui/icons-material";
import { exhaustive } from "exhaustive";
import colors from "styles/colors";

interface IProps {
    onSubmit: (date: Date | null) => void;
    subscription: ISubscription;
}

export const CancelSubscriptionDatePicker: FC<IProps> = ({ onSubmit, subscription }) => {
    const { membership } = useContext(MembershipsContext);
    const { hasPermission } = usePermission();
    const [validation, setValidation] = useState({
        isValid: true,
        reasonTag: "",
    });
    const [warningMessage, setWarningMessage] = useState({
        text: "",
        color: colors.black,
    });

    const { earliest, officialEarliest } = useMemo(() => {
        const officialEarliest = parseISO(subscription.earliest_cancellation_date ?? "");
        const earliest = hasPermission("can_override_earliest_subscription") ? new Date() : officialEarliest;
        return { earliest, officialEarliest };
    }, [hasPermission, subscription.earliest_cancellation_date]);

    const [endDate, setEndDate] = useState<Date | null>(
        max([officialEarliest, subscription.end_date ? parseISO(subscription.end_date) : new Date()])
    );

    const setEndDateWarningText = useCallback(
        (newEndDate: Date) => {
            if (newEndDate && isBefore(newEndDate, subscription.start_date) && !subscription.not_invoiced) {
                setWarningMessage({text: "warningEarlierThanOfficialInvoiced", color: colors.errorRed});
            } else if (newEndDate && isBefore(newEndDate, subscription.start_date) && subscription.not_invoiced) {
                setWarningMessage({text: "warningEarlierThanOfficialNotInvoiced", color: colors.black});
            } else if (newEndDate && isBefore(newEndDate, officialEarliest)) {
                setWarningMessage({text: "warningEarlierThanOfficial", color: colors.black});
            } else {
                setWarningMessage({text: "", color: colors.black});
            }
        },
        [officialEarliest, subscription.not_invoiced, subscription.start_date]
    );

    const validateDataCallback = useCallback(
        async (date: Date, membershipId: number) => {
            const subscriptionStartDate = new Date(subscription.start_date);

            if (!date) {
                return setValidation({ isValid: true, reasonTag: "" });
            }
            if (!isValid(date)) {
                return setValidation({ isValid: false, reasonTag: "errorDate" });
            }

            if (subscriptionStartDate > date) {
                date = subscriptionStartDate;
            }

            const response = await fetchProductsAvailability(membershipId, date);
            exhaustive(response, "responseType", {
                Success: (it) => {
                    if (!isAfter(date, sub(earliest, { days: 1 }))) {
                        return setValidation({ isValid: false, reasonTag: "errorMinDate" });
                    }
                    if (it.data.total_subscriptions - 1 < it.data.total_office_slots) {
                        return setValidation({ isValid: false, reasonTag: "notEnoughSubscriptionsError" });
                    }
                    return setValidation({ isValid: true, reasonTag: "" });
                },
                Error: () => {
                    return setValidation({ isValid: false, reasonTag: "" });
                },
            });
        },
        [earliest, subscription.start_date]
    );

    useEffect(() => {
        if (membership?.id) {
            validateDataCallback(parseISO(subscription.earliest_cancellation_date ?? ""), membership.id);
        }
        setEndDateWarningText(endDate as Date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membership?.id]);

    if (!membership) {
        return null;
    }

    const handleSubmit = (event: TFormEvent): void => {
        event.preventDefault();
        onSubmit(endDate);
    };

    const onChange = (date: unknown): void => {
        validateDataCallback(date as Date, membership?.id);
        setEndDate(date as Date);
        setEndDateWarningText(date as Date);
    };

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <List>
                    <ListItem>
                        <ListItemText primary={<LocalizeText tag="selectEndDateSubscription" />} />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary={<LocalizeText tag="removeFromSpaceflowInfo" />} />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={
                                <>
                                    <LocalizeText tag="earliestSubscriptionEndDate" />:{" "}
                                    {format(officialEarliest, dateFormats.WEBDATE)}
                                </>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <DatePicker
                            minDate={earliest}
                            format={dateFormats.WEBDATE}
                            label={<LocalizeText tag="endDate" />}
                            value={endDate}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !validation.isValid,
                                    helperText: validation.reasonTag && (
                                        <LocalizeText tag={validation.reasonTag as TLanguageTag} />
                                    ),
                                },
                            }}
                            onChange={onChange}
                        />
                        <VKButton
                            variant="outlined"
                            sx={{
                                marginBottom: "auto",
                                marginLeft: "5px",
                                marginTop: "-1px",
                                height: "60px",
                                width: "60px",
                            }}
                            onClick={() => setEndDate(null)}
                        >
                            <Clear />
                        </VKButton>
                    </ListItem>

                    {warningMessage.text && (
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="body2" style={{ color: warningMessage.color }}>
                                        {<LocalizeText  tag={warningMessage.text as TLanguageTag} />}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    )}

                    <ListItem>
                        <VKButton
                            type="submit"
                            tag="proceed"
                            disabled={!validation.isValid}
                            rightAddon={<ArrowIcon />}
                            template="primary"
                        />
                    </ListItem>
                </List>
            </form>
        </Box>
    );
};
